import useSWRMutation from 'swr/mutation';
import { AsmPrintSettings, BadgePrintingData, SaveAsmBadgePrint, SavePrintJob } from '../api/dtos';
import { useSaveAsmBadge } from './useSaveAsmBadge';

export interface PrintBadgesResult {
    count: number;
    failedJobs: number;
    errors?: PrintBadgesError[];
    /** Registration ids that printed successfully. */
    printedIds: number[];
    /** Registration ids that were attempted but failed. */
    failedIds: number[];
    /** True when the run was stopped early via the abort signal. */
    cancelled: boolean;
}

export interface PrintBadgesError {
    message: string;
    registrationId?: number;
}

export interface PrintBadgesArgs {
    badges: BadgePrintingData[];
    printSettings: AsmPrintSettings;
    barTenderUrl: string;
    /** Aborts the remaining badges when triggered (Cancel button). */
    signal?: AbortSignal;
    /** Called after each badge with (completed, total) so the UI can show progress. */
    onProgress?: (completed: number, total: number) => void;
}

interface BarTenderResponse {
    Status?: string;
    Messages?: unknown;
}

export const getBarTenderProblemMessages = (messages: unknown): string[] => {
    if (!Array.isArray(messages)) {
        return [];
    }

    return messages
        .filter((message): message is string => typeof message === 'string')
        .filter(
            (message) =>
                /\[Error\]/i.test(message) ||
                (/\[Warning\]/i.test(message) &&
                    /\b(?:an error occurred|image error\s*-?\d+)\b/i.test(message))
        )
        .map((message) => {
            const match = message.match(
                /^\[[^\]]+\]\s*\[(?:Error|Warning)\]\s*([\s\S]*)$/i
            );
            return (match?.[1] ?? message).trim();
        });
};

async function printBadgesFetcher(
    _key: string,
    { arg }: { arg: PrintBadgesArgs }
): Promise<PrintBadgesResult> {
    const { badges, printSettings, barTenderUrl, signal, onProgress } = arg;

    // Validation
    if (!printSettings) {
        throw new Error('Printer is required');
    }

    if (!badges || badges.length === 0) {
        throw new Error('No badges to print');
    }

    if (!printSettings.printer) {
        throw new Error('Printer is required');
    }

    if (!barTenderUrl) {
        throw new Error('Printing API URL is not configured');
    }

    let count = 0;
    let failedJobs = 0;
    let cancelled = false;
    const errorList: PrintBadgesError[] = [];
    const printedIds: number[] = [];
    const failedIds: number[] = [];

    // Sort badges alphabetically by last name, then first name
    const sortedBadges = [...badges].sort((a, b) => {
        const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.firstName || '').localeCompare(b.firstName || '');
    });

    const total = sortedBadges.length;

    for (let i = 0; i < sortedBadges.length; i++) {
        const badge = sortedBadges[i];

        // Stop before starting the next badge if the user cancelled.
        if (signal?.aborted) {
            cancelled = true;
            break;
        }

        try {
            // Create data sources dictionary
            const dataSources: { [key: string]: string } = {
                FULL_NAME: badge.fullName || '',
                ADDRESS: badge.adress || '',
                TITLE: badge.title || '',
                FIRST_NAME: badge.firstName || '',
                LAST_NAME: badge.lastName || '',
                INSTITUTION: badge.institution || '',
                SPECIALITY: badge.speciality || '',
                QR_CODE: badge.qrCode || '',
                EMAIL: badge.email || '',
                YEARS_ATTENDED: (badge.yearsAttended || 0).toString(),
                DELEGATE_TYPE: badge.delegateType || '',
                BADGE_IMAGE_TO_USE: badge.badgeImageToUse ?? 'NON_MEMBER.png', // Default image if none provided
            };

            // Create BarTender payload
            const payloadBadgeFront = {
                PrintBTWAction: {
                    Document: printSettings.labelFilePath,
                    Printer: printSettings.printer,
                    PrintToFileFolder: printSettings.printToFileFolder,
                    // Required when printing to PDF — BarTender needs the filename to write the output file
                    ...(printSettings.printer === 'PDF' && {
                        PrintToFileFileName: `${badge.lastName || 'badge'}.pdf`,
                    }),
                    SaveAfterPrint: true,
                    NamedDataSources: dataSources,
                },
            };

            // Send to BarTender API`
            const response = await fetch(barTenderUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payloadBadgeFront),
                signal,
            });

            if (!response.ok) {
                failedJobs++;
                if (badge.id != null) failedIds.push(badge.id);
                errorList.push({
                    message: `Failed to print badge: ${response.statusText}`,
                    registrationId: badge.id,
                });
            } else {
                // BarTender can report a completed action while warning that the
                // resulting badge is incomplete, such as when an image is missing.
                const responseData: BarTenderResponse = await response.json();
                const problemMessages = getBarTenderProblemMessages(responseData.Messages);

                if (responseData.Status === 'Faulted' || problemMessages.length > 0) {
                    failedJobs++;
                    if (badge.id != null) failedIds.push(badge.id);

                    errorList.push({
                        message:
                            problemMessages.join('\n\n') ||
                            'Print job failed with unknown error',
                        registrationId: badge.id,
                    });
                } else {
                    count++;
                    if (badge.id != null) printedIds.push(badge.id);
                }
            }
        } catch (error: any) {
            // A cancel aborts the in-flight fetch — treat it as a clean stop, not a failure.
            if (signal?.aborted || error?.name === 'AbortError') {
                cancelled = true;
                break;
            }
            failedJobs++;
            if (badge.id != null) failedIds.push(badge.id);
            errorList.push({
                message: error?.message || 'Unknown error while printing badge',
                registrationId: badge.id,
            });
        }

        onProgress?.(i + 1, total);
    }

    return {
        count,
        failedJobs,
        errors: errorList,
        printedIds,
        failedIds,
        cancelled,
    };
}

export const usePrintAsmBadges = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        'PrintASMBadges',
        printBadgesFetcher
    );

    const { saveAsmBadge } = useSaveAsmBadge();

    const printBadgesAndSave = async (args: PrintBadgesArgs) => {
        const result = await trigger(args);

        if (result) {
            // Only record badges that were actually attempted. Badges skipped by a
            // cancel are left untouched so they still show as unprinted.
            const printJobs = [
                ...result.printedIds.map(registrationId => new SavePrintJob({
                    registrationId,
                    printed: true,
                    failed: false
                })),
                ...result.failedIds.map(registrationId => new SavePrintJob({
                    registrationId,
                    printed: false,
                    failed: true
                }))
            ];

            if (printJobs.length > 0) {
                try {
                    await saveAsmBadge(new SaveAsmBadgePrint({
                        printJobs: printJobs
                    }));
                } catch (saveError) {
                    console.error('Failed to save badge print status:', saveError);
                }
            }
        }

        return result;
    };

    return {
        printBadges: printBadgesAndSave,
        data,
        error,
        pending: isMutating,
        response: data,
        errors: data?.errors || [],
    };
};
export default usePrintAsmBadges;

